import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LuBarChart3,
    LuBuilding2,
    LuChevronDown,
    LuChevronRight,
    LuClipboardCheck,
    LuContainer,
    LuFileInput,
    LuFuel,
    LuKanban,
    LuLayoutDashboard,
    LuPackage,
    LuLandmark,
    LuMap,
    LuMapPin,
    LuPanelLeftClose,
    LuPanelLeftOpen,
    LuPin,
    LuReceipt,
    LuSettings2,
    LuShieldCheck,
    LuTruck,
    LuUser,
    LuUserPlus,
    LuUsers,
    LuUsers2,
    LuWallet,
    LuWarehouse,
    LuX,
} from 'react-icons/lu';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import { can, retrieveCustomization, storeCustomizations } from '../../supplementary_components/userDataDelegate';
import {
    MENU_COLLAPSED_CATEGORIES_KEY,
    MENU_COLLAPSED_KEY,
    canSeePath,
    collapsedRail,
    menuTitle,
    pinPath,
    sanitizePins,
    unpinPath,
    visibleGroups,
    visibleShortcutPins,
} from './menuModel';
import styles from './mainMenu.module.scss';

const ICONS = {
    dashboard: LuLayoutDashboard,
    mainboard: LuKanban,
    loads: LuPackage,
    tenders: LuFileInput,
    brokers: LuBuilding2,
    invoices: LuReceipt,
    statements: LuWallet,
    trucks: LuTruck,
    trailers: LuContainer,
    'trailer-management': LuMapPin,
    'fuel-cards': LuFuel,
    inspections: LuClipboardCheck,
    drivers: LuUser,
    hiring: LuUserPlus,
    reports: LuBarChart3,
    employees: LuUsers,
    teams: LuUsers2,
};

const CATEGORY_ICONS = {
    dispatching: LuMap,
    accounting: LuLandmark,
    fleet: LuWarehouse,
    safety: LuShieldCheck,
    admin: LuSettings2,
    reports: LuBarChart3,
};

const see = (path) => canSeePath(path, can);
const SELECTION_KEY = 'menuSelectionSource';

const readSelection = () => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(SELECTION_KEY));
        if (parsed && (parsed.source === 'shortcut' || parsed.source === 'category') && typeof parsed.path === 'string') {
            return parsed;
        }
    } catch (_) {
        return null;
    }
    return null;
};

const MenuRow = ({ path, selected, pinControl, iconOnly, onTogglePin, onNavigate, onShowTip, onHideTip }) => {
    const title = menuTitle(path);
    const Icon = ICONS[path] || (String(path).startsWith('reports/') ? LuBarChart3 : null);
    const showTip = iconOnly
        ? (event) => onShowTip(title, event.currentTarget)
        : undefined;
    return (
        <div className={`${styles.row} ${selected ? styles.selected : ''}`}>
            <Link
                to={`/${path}`}
                className={styles.link}
                aria-label={title}
                aria-current={selected ? 'page' : undefined}
                onClick={() => onNavigate(path)}
                onMouseEnter={showTip}
                onMouseLeave={iconOnly ? onHideTip : undefined}
                onFocus={showTip}
                onBlur={iconOnly ? onHideTip : undefined}
            >
                {Icon ? <Icon className={styles.icon} aria-hidden="true" /> : null}
                <span className={styles.label}>{title}</span>
            </Link>
            {pinControl === 'pin' ? (
                <button
                    type="button"
                    className={styles.pin}
                    aria-label={`Pin ${title}`}
                    onClick={() => onTogglePin(path)}
                >
                    <LuPin aria-hidden="true" />
                </button>
            ) : null}
            {pinControl === 'unpin' ? (
                <button
                    type="button"
                    className={`${styles.pin} ${styles.unpin}`}
                    aria-label={`Unpin ${title}`}
                    onClick={() => onTogglePin(path)}
                >
                    <LuX aria-hidden="true" />
                </button>
            ) : null}
        </div>
    );
};

export const MainMenu = ({ path }) => {
    const [pins, setPins] = useState(() => sanitizePins(retrieveCustomization('menuPins')));
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(MENU_COLLAPSED_KEY) === 'true');
    const [collapsedCategories, setCollapsedCategories] = useState(() => {
        try {
            const parsed = JSON.parse(localStorage.getItem(MENU_COLLAPSED_CATEGORIES_KEY));
            return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
        } catch (_) {
            return [];
        }
    });
    const [hint, setHint] = useState('');
    const [selection, setSelection] = useState(readSelection);
    const [openGroup, setOpenGroup] = useState(null);
    const [tip, setTip] = useState(null);
    const hintTimer = useRef(null);
    const flyoutTimer = useRef(null);

    useEffect(() => () => {
        if (hintTimer.current) clearTimeout(hintTimer.current);
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    }, []);

    useEffect(() => {
        const syncPins = () => setPins(sanitizePins(retrieveCustomization('menuPins')));
        window.addEventListener('customizations-changed', syncPins);
        return () => window.removeEventListener('customizations-changed', syncPins);
    }, []);

    const showHint = (message) => {
        setHint(message);
        if (hintTimer.current) clearTimeout(hintTimer.current);
        hintTimer.current = setTimeout(() => setHint(''), 4000);
    };

    const persistPins = (next, previous) => {
        setPins(next);
        sendRequest(
            'PATCH',
            API_ROOT + 'employees/customizations',
            null,
            (updated) => {
                storeCustomizations(updated);
                setPins(sanitizePins(updated?.menuPins));
            },
            () => {
                setPins(previous);
                addNotification({ message: 'Could not save shortcut', type: 'alert' });
            },
            { menuPins: next },
        );
    };

    const togglePin = (target) => {
        if (pins.includes(target)) {
            persistPins(unpinPath(pins, target), pins);
            return;
        }
        const result = pinPath(pins, target, see);
        if (result.error) {
            showHint(result.error);
            return;
        }
        if (!result.changed) return;
        persistPins(result.pins, pins);
    };

    const toggleCategory = (id) => {
        setCollapsedCategories((current) => {
            const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
            localStorage.setItem(MENU_COLLAPSED_CATEGORIES_KEY, JSON.stringify(next));
            return next;
        });
    };

    const toggleCollapsed = () => {
        setOpenGroup(null);
        setCollapsed((current) => {
            const next = !current;
            localStorage.setItem(MENU_COLLAPSED_KEY, next ? 'true' : 'false');
            return next;
        });
    };

    const visiblePins = visibleShortcutPins(pins, see);
    const groups = visibleGroups(see);
    const pinnedSet = new Set(visiblePins);
    const showShortcuts = visiblePins.length > 0;
    const shortcutVisible = visiblePins.includes(path);
    const openedFromShortcut = selection?.path === path && selection?.source === 'shortcut' && shortcutVisible;

    const rememberSelection = (itemPath, source) => {
        const next = { path: itemPath, source };
        sessionStorage.setItem(SELECTION_KEY, JSON.stringify(next));
        setSelection(next);
        setOpenGroup(null);
    };

    const pinControlFor = (itemPath, source) => {
        if (itemPath === 'dashboard') return 'none';
        if (source === 'shortcut') return pinnedSet.has(itemPath) ? 'unpin' : 'pin';
        return pinnedSet.has(itemPath) ? 'none' : 'pin';
    };

    const showTip = (text, target) => {
        const rect = target.getBoundingClientRect();
        setTip({ text, top: rect.top + rect.height / 2, left: rect.right + 10 });
    };

    const renderRow = (itemPath, source, pinControl = pinControlFor(itemPath, source), iconOnly = false) => (
        <MenuRow
            key={`${source}-${itemPath}`}
            path={itemPath}
            selected={itemPath === path && (source === 'shortcut' ? openedFromShortcut : !openedFromShortcut)}
            pinControl={pinControl}
            iconOnly={iconOnly}
            onTogglePin={togglePin}
            onNavigate={() => {
                setTip(null);
                rememberSelection(itemPath, source);
            }}
            onShowTip={showTip}
            onHideTip={() => setTip(null)}
        />
    );

    const showFlyout = (group, target) => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
        const rect = target.getBoundingClientRect();
        setOpenGroup({
            id: group.id,
            label: group.label,
            items: group.items,
            top: rect.top,
            left: rect.right + 8,
        });
    };

    const hideFlyoutSoon = () => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
        flyoutTimer.current = setTimeout(() => setOpenGroup(null), 140);
    };

    const keepFlyout = () => {
        if (flyoutTimer.current) clearTimeout(flyoutTimer.current);
    };

    return (
        <nav className={`${styles.nav} ${collapsed ? styles.collapsed : ''}`} aria-label="Main menu">
            <div className={styles.list}>
                {collapsed ? collapsedRail(visiblePins, groups).map((entry, index) => {
                    if (entry.kind === 'gap') {
                        return <div key={`gap-${index}`} className={entry.size === 'afterPins' ? styles.shortcutGap : styles.gap} />;
                    }
                    if (entry.kind === 'rule') {
                        return <div key={`rule-${index}`} className={styles.shortcutRule} aria-hidden="true" />;
                    }
                    if (entry.kind === 'page' || entry.items.length === 1) {
                        const itemPath = entry.kind === 'page' ? entry.path : entry.items[0];
                        const source = entry.kind === 'page' ? entry.source : 'category';
                        return renderRow(itemPath, source, 'none', true);
                    }
                    const Icon = CATEGORY_ICONS[entry.id];
                    const categoryCurrent = entry.items.includes(path) && !openedFromShortcut;
                    return (
                        <button
                            key={entry.id}
                            type="button"
                            className={`${styles.categoryButton} ${categoryCurrent ? styles.categoryCurrent : ''}`}
                            aria-label={entry.label}
                            aria-expanded={openGroup?.id === entry.id}
                            onMouseEnter={(event) => showFlyout(entry, event.currentTarget)}
                            onMouseLeave={hideFlyoutSoon}
                            onFocus={(event) => showFlyout(entry, event.currentTarget)}
                            onBlur={hideFlyoutSoon}
                        >
                            {Icon ? <Icon className={styles.icon} aria-hidden="true" /> : null}
                        </button>
                    );
                }) : (
                    <>
                        {renderRow('dashboard', 'category')}
                        {showShortcuts ? (
                            <section className={styles.group} aria-label="Shortcuts">
                                <div className={styles.groupLabel}>Shortcuts</div>
                                {visiblePins.map((itemPath) => renderRow(itemPath, 'shortcut'))}
                                {hint ? <div className={styles.hint} role="status">{hint}</div> : null}
                            </section>
                        ) : null}
                        {groups.map((group) => {
                            const categoryClosed = Boolean(group.label) && collapsedCategories.includes(group.id);
                            return (
                            <section key={group.id} className={styles.group} aria-label={group.label || menuTitle(group.items[0])}>
                                {group.label ? (
                                    <button
                                        type="button"
                                        className={styles.groupLabel}
                                        aria-expanded={!categoryClosed}
                                        onClick={() => toggleCategory(group.id)}
                                    >
                                        <span>{group.label}</span>
                                        {categoryClosed ? <LuChevronRight aria-hidden="true" /> : <LuChevronDown aria-hidden="true" />}
                                    </button>
                                ) : <div className={styles.standalone} />}
                                {categoryClosed ? null : group.items.map((itemPath) => renderRow(itemPath, 'category'))}
                            </section>
                            );
                        })}
                    </>
                )}
            </div>
            {openGroup ? (
                <div
                    className={styles.flyout}
                    style={{ top: openGroup.top, left: openGroup.left }}
                    onMouseEnter={keepFlyout}
                    onMouseLeave={hideFlyoutSoon}
                >
                    <div className={styles.flyoutTitle}>{openGroup.label}</div>
                    {openGroup.items.map((itemPath) => renderRow(itemPath, 'category', 'none'))}
                </div>
            ) : null}
            {tip ? (
                <div className={styles.tip} style={{ top: tip.top, left: tip.left }} role="tooltip">
                    {tip.text}
                </div>
            ) : null}
            <button
                type="button"
                className={styles.collapse}
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                title={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
                {collapsed ? <LuPanelLeftOpen className={styles.icon} aria-hidden="true" /> : <LuPanelLeftClose className={styles.icon} aria-hidden="true" />}
                <span className={styles.collapseLabel}>{collapsed ? 'Expand' : 'Collapse'}</span>
            </button>
        </nav>
    );
};
